/**
 * DraftedRosterModal — post-player-draft roster review + lineup editor.
 *
 * Two-column layout:
 *   • Your roster (left)  — editable: drag pitching rotation, drag-to-reorder
 *     batting slots with dropdowns, bench overview. Changes auto-save.
 *   • Opponent (right)    — read-only mirror of the same pitcher/batting/bench
 *     structure, reflecting whatever they last saved.
 *
 * Lineup save is optional — the server keeps the default draft-pick-order
 * batting if untouched. The PUT /games/{id}/teams/{role}/lineup endpoint
 * persists batting_order + pitcher_order.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { X, Info, GripVertical } from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { PlayerDetailsDialog } from "@/components/game";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LINEUP_SLOTS = ["C", "1B", "2B", "3B", "SS", "LF", "CF", "RF", "DH"];

function yearSpan(player) {
  const s = player?.seasons;
  if (!s || s.length === 0) return player?.year ? String(player.year) : null;
  const first = s[0].year;
  const last = s[s.length - 1].year;
  return first === last ? String(first) : `${first}–${last}`;
}

function expandedPositions(player) {
  const all = (player?.natural_positions?.length > 0)
    ? player.natural_positions
    : [player?.position].filter(Boolean);
  return new Set(all.flatMap(p => p === "OF" ? ["LF", "CF", "RF"] : [p]));
}

function isEligibleForSlot(slot, player) {
  if (!player || player.position === "P") return false;
  if (slot === "DH") return true;
  return expandedPositions(player).has(slot);
}

function displayPosition(player) {
  if (!player) return "";
  const pos = player.position;
  const natural = player.natural_positions || [];
  if (pos === "DH") {
    const secondary = natural.find(p => p !== "DH" && ["C","1B","2B","3B","SS","LF","CF","RF"].includes(p));
    return secondary ? `DH/${secondary}` : "DH";
  }
  if (pos === "OF") {
    const specific = natural.filter(p => ["LF","CF","RF"].includes(p));
    return specific.length > 0 ? specific.join("/") : "OF";
  }
  return pos;
}

function statLine(player) {
  const stats = player?.stats || {};
  if (player?.position === "P") {
    const era = stats.era != null ? Number(stats.era).toFixed(2) : "—";
    return `${stats.wins ?? 0}-${stats.losses ?? 0} · ERA ${era} · ${stats.strikeouts ?? 0} K`;
  }
  const avg = stats.batting_average != null
    ? Number(stats.batting_average).toFixed(3).replace(/^0/, "")
    : "—";
  return `AVG ${avg} · ${stats.home_runs ?? 0} HR · ${stats.rbi ?? 0} RBI`;
}

// Shared helper — build { slot: playerId } from a batting_order list + players array.
function buildAssignments(players, battingOrder = []) {
  const assignments = Object.fromEntries(LINEUP_SLOTS.map(s => [s, null]));
  const byId = Object.fromEntries(players.map(p => [p.id, p]));
  const used = new Set();
  for (const pid of battingOrder) {
    const p = byId[pid];
    if (!p || p.position === "P") continue;
    if (LINEUP_SLOTS.includes(p.position) && assignments[p.position] == null) {
      assignments[p.position] = pid; used.add(pid); continue;
    }
    if (p.position === "OF") {
      const preferred = (p.natural_positions || []).filter(np => ["LF","CF","RF"].includes(np));
      const order = [...new Set([...preferred, "LF", "CF", "RF"])];
      const slot = order.find(s => assignments[s] == null);
      if (slot) { assignments[slot] = pid; used.add(pid); continue; }
    }
    if (assignments.DH == null) { assignments.DH = pid; used.add(pid); }
  }
  for (const slot of LINEUP_SLOTS) {
    if (assignments[slot] != null) continue;
    const candidate = players.find(p => !used.has(p.id) && isEligibleForSlot(slot, p));
    if (candidate) { assignments[slot] = candidate.id; used.add(candidate.id); }
  }
  return assignments;
}

/* ─── Sortable pitcher row ───────────────────────────────────────────────── */
function SortablePitcherRow({ player, onPreview, isStarter }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: player.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  return (
    <li ref={setNodeRef} style={style}
      className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border ${
        isStarter ? "bg-amber-500/10 border-amber-500/40" : "bg-slate-800/60 border-slate-700"
      }`}>
      <button {...attributes} {...listeners}
        className="p-1 rounded text-slate-500 hover:text-slate-200 cursor-grab active:cursor-grabbing touch-none"
        title="Drag to reorder">
        <GripVertical size={12} />
      </button>
      <span className="text-[10px] font-black tracking-wider text-slate-500 w-8 shrink-0">
        {isStarter ? "SP" : "RP"}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-white truncate">{player.name}</p>
        {yearSpan(player) && <p className="text-[9px] text-slate-600">{yearSpan(player)}</p>}
        <p className="text-[10px] text-slate-500">
          ERA {player.stats?.era != null ? Number(player.stats.era).toFixed(2) : "—"}
          {" · "}{player.stats?.wins ?? 0}-{player.stats?.losses ?? 0}
        </p>
      </div>
      <button type="button" onClick={() => onPreview?.(player)} title="Career stats"
        className="p-1 rounded text-slate-500 hover:text-amber-400 hover:bg-slate-700/60 transition-colors">
        <Info size={13} />
      </button>
    </li>
  );
}

/* ─── Sortable batting-order row ─────────────────────────────────────────── */
function SortableLineupRow({ slot, idx, assignedId, player, candidates, onSetSlot, onPreview }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: slot });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  return (
    <li ref={setNodeRef} style={style}
      className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border ${
        player ? "bg-slate-800/60 border-slate-700" : "border-dashed border-slate-800"
      }`}>
      <button {...attributes} {...listeners}
        className="p-1 rounded text-slate-500 hover:text-slate-200 cursor-grab active:cursor-grabbing touch-none"
        title="Drag to reorder batting position">
        <GripVertical size={12} />
      </button>
      <span className="text-[9px] font-black tracking-wider text-slate-500 w-10 shrink-0">
        {idx + 1}. {slot}
      </span>
      <select
        value={assignedId || ""}
        onChange={e => onSetSlot(slot, e.target.value)}
        className="flex-1 min-w-0 bg-slate-900 border border-slate-700/60 rounded-md px-2 py-0.5 text-white text-xs font-bold focus:outline-none focus:border-amber-500/60"
      >
        <option value="">— empty —</option>
        {candidates.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      {player && (
        <button type="button" onClick={() => onPreview?.(player)} title="Career stats"
          className="p-0.5 text-slate-500 hover:text-amber-400 transition-colors shrink-0">
          <Info size={11} />
        </button>
      )}
      {player && yearSpan(player) && (
        <span className="text-[9px] text-slate-600 shrink-0 hidden sm:inline">{yearSpan(player)}</span>
      )}
    </li>
  );
}

/* ─── Editable roster column (your side) ────────────────────────────────── */
function EditableRosterColumn({ label, accent, team, gameId, role, onPreview }) {
  const players = useMemo(() => team?.players || [], [team]);

  const initialAssignments = useMemo(
    () => buildAssignments(players, team?.batting_order),
    [players, team?.batting_order],
  );
  const initialPitcherOrder = useMemo(
    () => players.filter(p => p.position === "P").map(p => p.id),
    [players],
  );

  const [assignments, setAssignments] = useState(initialAssignments);
  const [pitcherOrder, setPitcherOrder] = useState(initialPitcherOrder);
  const [slotOrder, setSlotOrder] = useState([...LINEUP_SLOTS]);
  const [saveStatus, setSaveStatus] = useState("idle");
  const debounceRef = useRef(null);

  // Sync from server state without fighting user mid-edit (deep-equality guard).
  useEffect(() => {
    setAssignments(prev => {
      const a = prev || {}, b = initialAssignments || {};
      const ka = Object.keys(a), kb = Object.keys(b);
      if (ka.length !== kb.length) return initialAssignments;
      for (const k of kb) if (a[k] !== b[k]) return initialAssignments;
      return prev;
    });
  }, [initialAssignments]);
  useEffect(() => {
    setPitcherOrder(prev => {
      if (prev.length !== initialPitcherOrder.length) return initialPitcherOrder;
      for (let i = 0; i < prev.length; i++) if (prev[i] !== initialPitcherOrder[i]) return initialPitcherOrder;
      return prev;
    });
  }, [initialPitcherOrder]);
  // Reset slot order when team changes (redraft).
  useEffect(() => { setSlotOrder([...LINEUP_SLOTS]); }, [initialAssignments]);

  const playersById = useMemo(() => Object.fromEntries(players.map(p => [p.id, p])), [players]);
  const assignedIds = new Set(Object.values(assignments).filter(Boolean));
  const benchPlayers = players.filter(p => p.position !== "P" && !assignedIds.has(p.id));
  const eligibleFor = (slot) =>
    players.filter(p => isEligibleForSlot(slot, p) && (assignments[slot] === p.id || !assignedIds.has(p.id)));

  // Auto-save 600ms after last change.
  const isInitial = useRef(true);
  useEffect(() => {
    if (isInitial.current) { isInitial.current = false; return; }
    if (!gameId) return;
    clearTimeout(debounceRef.current);
    setSaveStatus("saving");
    const snapshot = { assignments: { ...assignments }, pitcherOrder: [...pitcherOrder], slotOrder: [...slotOrder] };
    debounceRef.current = setTimeout(async () => {
      try {
        await axios.put(`${API}/games/${gameId}/teams/${role}/lineup`, {
          batting_order: snapshot.slotOrder.map(s => snapshot.assignments[s]).filter(Boolean),
          pitcher_order: snapshot.pitcherOrder,
        });
        setSaveStatus("saved");
      } catch {
        setSaveStatus("idle");
        toast.error("Couldn't save lineup");
      }
    }, 600);
    return () => clearTimeout(debounceRef.current);
  }, [assignments, pitcherOrder, slotOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  const setSlot = (slot, playerId) => {
    setAssignments(prev => {
      const next = { ...prev };
      for (const s of LINEUP_SLOTS) { if (next[s] === playerId && s !== slot) next[s] = null; }
      next[slot] = playerId || null;
      return next;
    });
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const handlePitcherDragEnd = useCallback((e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setPitcherOrder(prev => {
      const oi = prev.indexOf(active.id), ni = prev.indexOf(over.id);
      return (oi < 0 || ni < 0) ? prev : arrayMove(prev, oi, ni);
    });
  }, []);

  const handleLineupDragEnd = useCallback((e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setSlotOrder(prev => {
      const oi = prev.indexOf(active.id), ni = prev.indexOf(over.id);
      return (oi < 0 || ni < 0) ? prev : arrayMove(prev, oi, ni);
    });
  }, []);

  const filledSlots = slotOrder.filter(s => assignments[s]).length;
  const borderClass = accent === "home" ? "border-amber-500/40" : "border-blue-500/40";
  const headerBorder = accent === "home" ? "border-amber-500/30" : "border-blue-500/30";
  const labelTone = accent === "home" ? "text-amber-400" : "text-blue-400";

  return (
    <div className={`min-w-0 rounded-xl border bg-slate-900/40 ${borderClass}`}>
      <div className={`px-3 py-2 border-b ${headerBorder} flex items-center justify-between`}>
        <p className={`text-[10px] font-black uppercase tracking-widest ${labelTone}`}>{label}</p>
        <p className={`text-[9px] font-bold transition-colors ${
          saveStatus === "saving" ? "text-amber-400" :
          saveStatus === "saved"  ? "text-emerald-400" :
          "text-slate-600 italic font-normal"
        }`}>
          {saveStatus === "saving" ? "Saving…" : saveStatus === "saved" ? "✓ Saved" : "auto-saves on change"}
        </p>
      </div>

      <div className="p-3 space-y-4">
        {/* Pitching rotation */}
        <div className="space-y-1.5">
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-500">
            Pitching Rotation <span className="normal-case font-medium text-slate-700">drag to set starter</span>
          </p>
          {pitcherOrder.length === 0 ? (
            <p className="text-[10px] text-slate-600 italic">No pitchers drafted.</p>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handlePitcherDragEnd}>
              <SortableContext items={pitcherOrder} strategy={verticalListSortingStrategy}>
                <ul className="space-y-1">
                  {pitcherOrder.map((pid, i) => {
                    const p = playersById[pid];
                    return p ? <SortablePitcherRow key={pid} player={p} onPreview={onPreview} isStarter={i === 0} /> : null;
                  })}
                </ul>
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* Batting order — drag rows to reorder */}
        <div className="space-y-1.5">
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-500">
            Batting Order <span className="normal-case font-medium text-slate-700">{filledSlots}/9 · drag to reorder</span>
          </p>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleLineupDragEnd}>
            <SortableContext items={slotOrder} strategy={verticalListSortingStrategy}>
              <ul className="space-y-1">
                {slotOrder.map((slot, idx) => (
                  <SortableLineupRow
                    key={slot}
                    slot={slot}
                    idx={idx}
                    assignedId={assignments[slot]}
                    player={assignments[slot] ? playersById[assignments[slot]] : null}
                    candidates={eligibleFor(slot)}
                    onSetSlot={setSlot}
                    onPreview={onPreview}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        </div>

        {/* Bench */}
        {benchPlayers.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-500">
              Bench <span className="normal-case font-medium text-slate-700">{benchPlayers.length}</span>
            </p>
            <ul className="space-y-1">
              {benchPlayers.map(p => (
                <li key={p.id} className="flex items-center gap-2 px-2 py-1 rounded-md bg-slate-800/40 border border-slate-800">
                  <span className="text-[10px] font-mono font-black text-slate-500 shrink-0">{displayPosition(p)}</span>
                  <span className="text-xs text-slate-300 truncate flex-1">{p.name}</span>
                  <button type="button" onClick={() => onPreview?.(p)} title="Career stats"
                    className="p-0.5 text-slate-500 hover:text-amber-400 transition-colors">
                    <Info size={11} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Read-only roster column (opponent side) ────────────────────────────── */
function RosterColumn({ label, accent, team, onPreview }) {
  const players = useMemo(() => team?.players || [], [team]);
  const assignments = useMemo(
    () => buildAssignments(players, team?.batting_order),
    [players, team?.batting_order],
  );
  const playersById = useMemo(() => Object.fromEntries(players.map(p => [p.id, p])), [players]);
  const pitchers = players.filter(p => p.position === "P");
  const assignedIds = new Set(Object.values(assignments).filter(Boolean));
  const benchPlayers = players.filter(p => p.position !== "P" && !assignedIds.has(p.id));
  const filledSlots = LINEUP_SLOTS.filter(s => assignments[s]).length;

  const borderClass = accent === "home" ? "border-amber-500/40" : "border-blue-500/40";
  const headerBorder = accent === "home" ? "border-amber-500/30" : "border-blue-500/30";
  const labelTone = accent === "home" ? "text-amber-400" : "text-blue-400";

  if (players.length === 0) {
    return (
      <div className={`min-w-0 rounded-xl border bg-slate-900/40 ${borderClass}`}>
        <div className={`px-3 py-2 border-b ${headerBorder}`}>
          <p className={`text-[10px] font-black uppercase tracking-widest ${labelTone}`}>{label}</p>
        </div>
        <p className="text-[10px] text-slate-600 italic text-center py-8">No players drafted.</p>
      </div>
    );
  }

  return (
    <div className={`min-w-0 rounded-xl border bg-slate-900/40 ${borderClass}`}>
      <div className={`px-3 py-2 border-b ${headerBorder} flex items-baseline justify-between`}>
        <p className={`text-[10px] font-black uppercase tracking-widest ${labelTone}`}>{label}</p>
        <p className="text-[9px] text-slate-600 italic">read-only</p>
      </div>

      <div className="p-3 space-y-4">
        {/* Pitching rotation */}
        <div className="space-y-1.5">
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-500">
            Pitching Rotation
          </p>
          {pitchers.length === 0 ? (
            <p className="text-[10px] text-slate-600 italic">No pitchers drafted.</p>
          ) : (
            <ul className="space-y-1">
              {pitchers.map((p, i) => (
                <li key={p.id}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border ${
                    i === 0 ? "bg-amber-500/10 border-amber-500/40" : "bg-slate-800/60 border-slate-700"
                  }`}>
                  <span className="text-[10px] font-black tracking-wider text-slate-500 w-8 shrink-0">
                    {i === 0 ? "SP" : "RP"}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-white truncate">{p.name}</p>
                    {yearSpan(p) && <p className="text-[9px] text-slate-600">{yearSpan(p)}</p>}
                    <p className="text-[10px] text-slate-500">
                      ERA {p.stats?.era != null ? Number(p.stats.era).toFixed(2) : "—"}
                      {" · "}{p.stats?.wins ?? 0}-{p.stats?.losses ?? 0}
                    </p>
                  </div>
                  <button type="button" onClick={() => onPreview?.(p)} title="Career stats"
                    className="p-1 rounded text-slate-500 hover:text-amber-400 hover:bg-slate-700/60 transition-colors">
                    <Info size={13} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Batting order — read-only */}
        <div className="space-y-1.5">
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-500">
            Batting Order <span className="normal-case font-medium text-slate-700">{filledSlots}/9</span>
          </p>
          <ul className="space-y-1">
            {LINEUP_SLOTS.map((slot, idx) => {
              const pid = assignments[slot];
              const p = pid ? playersById[pid] : null;
              return (
                <li key={slot} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border ${
                  p ? "bg-slate-800/60 border-slate-700" : "border-dashed border-slate-800"
                }`}>
                  <span className="text-[9px] font-black tracking-wider text-slate-500 w-10 shrink-0">
                    {idx + 1}. {slot}
                  </span>
                  {p ? (
                    <>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-white truncate">{p.name}</p>
                        {yearSpan(p) && <p className="text-[9px] text-slate-600">{yearSpan(p)}</p>}
                      </div>
                      <button type="button" onClick={() => onPreview?.(p)} title="Career stats"
                        className="p-0.5 text-slate-500 hover:text-amber-400 transition-colors shrink-0">
                        <Info size={11} />
                      </button>
                    </>
                  ) : (
                    <span className="text-[9px] text-slate-700 italic">empty</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        {/* Bench */}
        {benchPlayers.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-500">
              Bench <span className="normal-case font-medium text-slate-700">{benchPlayers.length}</span>
            </p>
            <ul className="space-y-1">
              {benchPlayers.map(p => (
                <li key={p.id} className="flex items-center gap-2 px-2 py-1 rounded-md bg-slate-800/40 border border-slate-800">
                  <span className="text-[10px] font-mono font-black text-slate-500 shrink-0">{displayPosition(p)}</span>
                  <span className="text-xs text-slate-300 truncate flex-1">{p.name}</span>
                  <button type="button" onClick={() => onPreview?.(p)} title="Career stats"
                    className="p-0.5 text-slate-500 hover:text-amber-400 transition-colors">
                    <Info size={11} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Modal ──────────────────────────────────────────────────────────────── */
export default function DraftedRosterModal({ open, onClose, homeTeam, awayTeam, role = "home", gameId }) {
  const [previewPlayer, setPreviewPlayer] = useState(null);

  if (!open) return null;

  const myTeam = role === "away" ? awayTeam : homeTeam;
  const oppTeam = role === "away" ? homeTeam : awayTeam;
  const myLabel = role === "away" ? "Your Roster (Away)" : "Your Roster (Home)";
  const oppLabel = role === "away" ? "Opponent (Home)" : "Opponent (Away)";
  const myAccent = role === "away" ? "away" : "home";
  const oppAccent = role === "away" ? "home" : "away";

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
    >
      <motion.div
        initial={{ scale: 0.94, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.18 }}
        onClick={e => e.stopPropagation()}
        className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-3xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden"
      >
        <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between shrink-0">
          <div className="min-w-0">
            <p className="text-[10px] font-black tracking-widest text-amber-400 uppercase">Drafted Rosters</p>
            <p className="text-xs text-slate-400">Drag pitchers to set rotation · drag batting rows to reorder · changes save automatically.</p>
          </div>
          <button type="button" onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-500 hover:text-white transition-colors"
            title="Close">
            <X size={14} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
            <EditableRosterColumn
              label={myLabel}
              accent={myAccent}
              team={myTeam}
              gameId={gameId}
              role={role}
              onPreview={setPreviewPlayer}
            />
            <RosterColumn
              label={oppLabel}
              accent={oppAccent}
              team={oppTeam}
              onPreview={setPreviewPlayer}
            />
          </div>
        </div>
      </motion.div>

      <PlayerDetailsDialog
        open={!!previewPlayer}
        onClose={() => setPreviewPlayer(null)}
        player={previewPlayer}
      />
    </div>
  );
}
